'use client'

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso'
import { ChatMessage } from './ChatMessage'
import { ChatLoader } from '@/components/ui/ChatLoader'
import { AssistantLockScreen } from './AssistantLockScreen'
import {
  Button,
  Card,
} from '@sarkiai/ui'
import {
  Bot,
  X,
  History,
  Plus,
  CreditCard,
  Building2,
  Mail,
  Trash2,
  ChevronRight,
  Loader,
  MessageCircle,
  ArrowDown,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslations } from '@/components/I18nProvider'
import { useAssistant } from './AssistantContext'
import { ChatSession } from '@/hooks/useChatHistory'

interface AssistantMessageListProps {
  messages: any[]
  status: string
  sessions?: ChatSession[]
  virtuosoRef: React.RefObject<VirtuosoHandle>
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void
  onAtBottomChange: (atBottom: boolean) => void
  onSuggestedPrompt: (prompt: string) => void
  onDeleteSession?: (id: string) => void
  onNewChat: () => void
  onClose: () => void
  isAnonymous?: boolean
  isLocked?: boolean
}

export function AssistantMessageList({
  messages,
  status,
  sessions = [],
  virtuosoRef,
  onScroll,
  onAtBottomChange,
  onSuggestedPrompt,
  onDeleteSession,
  onNewChat,
  onClose,
  isAnonymous = false,
  isLocked = false,
}: AssistantMessageListProps) {
  const t = useTranslations('chat')
  const {
    view,
    setView,
    loadChatById,
    isLoadingChat,
    isQuotaLockForced,
    setIsQuotaLockForced,
    lockDismissed,
    setLockDismissed,
  } = useAssistant()

  const [isAtBottom, setIsAtBottom] = useState(true)
  const [showScrollButton, setShowScrollButton] = useState(false)

  const onDismissLock = () => {
    setLockDismissed(true)
    setIsQuotaLockForced(false)
  }

  const scrollToBottom = useCallback(() => {
    virtuosoRef.current?.scrollToIndex({ index: messages.length - 1, align: 'end' })
  }, [messages.length, virtuosoRef])

  // Auto-scroll when messages update
  useEffect(() => {
    if (isAtBottom && messages.length > 0 && view === 'chat') {
      setTimeout(() => {
        virtuosoRef.current?.scrollToIndex({ index: messages.length - 1, align: 'end' })
      }, 250)
    }
  }, [messages, isAtBottom, view, virtuosoRef])

  // Stream scroll
  const isStreaming = status === 'streaming' || status === 'submitted'
  useEffect(() => {
    if (isStreaming && messages.length > 0 && isAtBottom && view === 'chat') {
      const scrollToBottomDuringStream = () => {
        virtuosoRef.current?.scrollToIndex({ index: messages.length - 1, align: 'end', behavior: 'smooth' })
      }
      scrollToBottomDuringStream()
      const interval = setInterval(scrollToBottomDuringStream, 250)
      return () => clearInterval(interval)
    }
  }, [messages, isStreaming, isAtBottom, view, virtuosoRef])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // @ts-expect-error: virtuoso event type mismatch
    const { scrollTop, scrollHeight, clientHeight } = e.target
    const atBottom = scrollHeight - scrollTop - clientHeight <= 50
    setIsAtBottom(atBottom)
    setShowScrollButton(!atBottom)
    onScroll(e)
  }

  const itemContent = useCallback((index: number, m: any) => (
    <div className={index === messages.length - 1 ? "pb-[12vh]" : ""}>
      <ChatMessage
        message={m}
        isLast={index === messages.length - 1}
        status={status}
      />
    </div>
  ), [messages.length, status])

  const suggestions = [
    { label: t('vcard.suggestion'), icon: CreditCard, prompt: "Comment puis-je créer une Djotta VCard ?" },
    { label: t('sarkiai.suggestion'), icon: Bot, prompt: "Quelles sont les capacités de SarkiAI ?" },
    { label: t('enterprise.suggestion'), icon: Building2, prompt: "Proposez-vous des solutions pour les entreprises ?" },
    { label: t('contact.suggestion'), icon: Mail, prompt: "Comment contacter l'équipe Djotta ?" },
  ]

  return (
    <Card className="flex-1 flex flex-col shadow-2xl border-2 border-primary/20 bg-background/95 backdrop-blur-lg overflow-hidden rounded-3xl p-0">
      {/* Header */}
      <div className="flex flex-col border-b border-border/50 bg-background/50 backdrop-blur-md">
        <div className="flex items-center justify-between p-2 pb-1.5 px-3">
          <div className="flex items-center gap-2">
            <div className="relative shrink-0">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20 shadow-sm" aria-hidden="true">
                <Bot className="h-4 w-4 text-primary" aria-hidden="true" />
              </div>
            </div>
            <span className="font-semibold text-sm sm:text-base text-foreground">SeA</span>
          </div>
          <div className="flex items-center gap-0.5">
            {(messages.length > 1 || view === 'history') && (
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 rounded-full hover:bg-primary/10 text-foreground"
                onClick={onNewChat}
                title={t('new_chat')}
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}

            <Button
              size="icon"
              variant="ghost"
              className={`h-8 w-8 rounded-full text-foreground ${view === 'history' ? 'bg-primary/20' : 'hover:bg-primary/10'}`}
              onClick={() => setView(view === 'history' ? 'chat' : 'history')}
              title={t('history_toggle')}
            >
              <History className="h-4 w-4" />
            </Button>

            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-full hover:bg-primary/10 text-foreground"
              onClick={onClose}
              title={t('hide')}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden relative p-2">
        <AnimatePresence mode="wait">
          {view === 'history' ? (
            <motion.div
              key="history"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="h-full flex flex-col p-2 space-y-2 overflow-y-auto"
            >
              <h4 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground px-2 py-1">{t('history')}</h4>
              {sessions.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center opacity-50 grayscale gap-2">
                  <History className="h-8 w-8" />
                  <p className="text-xs font-bold">{t('no_history')}</p>
                </div>
              ) : (
                sessions.map((session) => (
                  <div
                    key={session.id}
                    className="group relative flex items-center gap-2 p-3 rounded-2xl border border-transparent hover:border-primary/20 transition-all cursor-pointer bg-black/5 dark:bg-white/5"
                    onClick={() => {
                      loadChatById(session.id)
                      setView('chat')
                    }}
                  >
                    <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <MessageCircle className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate pr-6">{session.title || t('untitled_conversation')}</p>
                      <p className="text-[10px] text-muted-foreground font-medium">
                        {new Date(session.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                    {isLoadingChat ? (
                      <Loader className="h-4 w-4 text-muted-foreground animate-spin" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        if (window.confirm(t('delete_session_desc'))) {
                          onDeleteSession?.(session.id)
                        }
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-red-500/80 dark:text-red-400/80 hover:text-red-600 dark:hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))
              )}
            </motion.div>
          ) : isLoadingChat ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full flex flex-col gap-4 p-4"
            >
              {/* Shimmer skeleton — user message */}
              <div className="flex justify-end">
                <div className="w-[65%] h-10 rounded-2xl rounded-tr-none bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 dark:from-white/5 dark:via-white/10 dark:to-white/5 animate-pulse bg-[length:200%_100%]" />
              </div>
              {/* Shimmer skeleton — assistant message */}
              <div className="flex flex-col gap-2">
                <div className="w-[80%] h-4 rounded-full bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 dark:from-white/5 dark:via-white/10 dark:to-white/5 animate-pulse bg-[length:200%_100%]" />
                <div className="w-[60%] h-4 rounded-full bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 dark:from-white/5 dark:via-white/10 dark:to-white/5 animate-pulse bg-[length:200%_100%]" />
                <div className="w-[70%] h-4 rounded-full bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 dark:from-white/5 dark:via-white/10 dark:to-white/5 animate-pulse bg-[length:200%_100%]" />
              </div>
              {/* Shimmer skeleton — user message */}
              <div className="flex justify-end">
                <div className="w-[50%] h-10 rounded-2xl rounded-tr-none bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 dark:from-white/5 dark:via-white/10 dark:to-white/5 animate-pulse bg-[length:200%_100%]" />
              </div>
              {/* Shimmer skeleton — assistant message */}
              <div className="flex flex-col gap-2">
                <div className="w-[75%] h-4 rounded-full bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 dark:from-white/5 dark:via-white/10 dark:to-white/5 animate-pulse bg-[length:200%_100%]" />
                <div className="w-[55%] h-4 rounded-full bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 dark:from-white/5 dark:via-white/10 dark:to-white/5 animate-pulse bg-[length:200%_100%]" />
              </div>
            </motion.div>
          ) : messages.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full flex flex-col items-center justify-center gap-6 p-4"
            >
              <ChatLoader />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 w-full max-w-2xl">
                {suggestions.map((s, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    className="h-auto py-3 sm:py-4 px-4 text-left justify-start border-border/50 bg-card/50 hover:border-primary hover:bg-primary/10 dark:hover:bg-primary/20 transition-colors min-w-0 rounded-2xl"
                    onClick={() => onSuggestedPrompt(s.prompt)}
                  >
                    <s.icon className="h-3 w-3 sm:h-4 sm:w-4 mr-2 text-primary dark:text-primary shrink-0" />
                    <span className="text-xs text-foreground truncate">{s.label}</span>
                  </Button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full"
            >
              <Virtuoso
                ref={virtuosoRef}
                data={messages}
                itemContent={itemContent}
                atBottomStateChange={(atBottom) => {
                  setIsAtBottom(atBottom)
                  setShowScrollButton(!atBottom)
                  onAtBottomChange(atBottom)
                }}
                useWindowScroll={false}
                initialTopMostItemIndex={messages.length - 1}
                className="h-full custom-scrollbar overscroll-contain"
                followOutput={isAtBottom ? 'smooth' : false}
                onScroll={handleScroll}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {isQuotaLockForced && !lockDismissed && (
          <AssistantLockScreen onDismiss={onDismissLock} />
        )}

        <AnimatePresence>
          {showScrollButton && messages.length > 0 && view === 'chat' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 10 }}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10"
            >
              <Button
                size="icon"
                variant="default"
                className="rounded-full shadow-md opacity-90 hover:opacity-100"
                onClick={scrollToBottom}
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Card>
  )
}
